import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { FEATURED_GUESTS, UPCOMING } from '@/data/events';
import { EventRow } from '@/features/events';
import { fmtDateCompact, getCountdown, type Countdown } from '@/lib/format';
import { useI18n } from '@/providers/i18n';
import Marquee from '@/ui/Marquee';

import HomeHero from './components/HomeHero';
import HomeTimeline from './components/HomeTimeline';
import ResidentsPreview from './components/ResidentsPreview';
import SignalFeature from './components/SignalFeature';
import styles from './HomePage.module.css';

const NEXT = UPCOMING.find((e) => e.flagship) ?? UPCOMING[0];

export default function HomePage() {
  const { t } = useI18n();
  const [tick, setTick] = useState<Countdown>(() =>
    NEXT ? getCountdown(NEXT.date) : { d: '000', h: '00', m: '00', s: '00' },
  );

  useEffect(() => {
    if (!NEXT) return;
    const id = window.setInterval(() => setTick(getCountdown(NEXT.date)), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!NEXT) return null;

  return (
    <>
      <HomeHero nextEventId={NEXT.id} />

      <SignalFeature event={NEXT} countdown={tick} />

      <div className={styles.marquee}>
        <Marquee
          speed={45}
          items={[
            'MOCT 07 / IN FREQUENCY',
            fmtDateCompact(NEXT.date),
            'YEREVAN',
            'HAYFILM CLUSTER',
            '4 STAGES',
            '+',
            'TICKETS WAVE 01 OPEN',
            'STRICTLY 18+',
            'FACE CONTROL',
          ]}
        />
      </div>

      <section className="sec">
        <header className="sec-h">
          <span className="num">/ 02</span>
          <span className="title">{t('label.upcoming')}</span>
          <Link to="/events" className="meta">
            {t('cta.viewAll')} →
          </Link>
        </header>
        <div className={styles.list}>
          {UPCOMING.slice(0, 3).map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </div>
      </section>

      <section className="sec">
        <header className="sec-h">
          <span className="num">/ 03</span>
          <span className="title">{t('label.featured')}</span>
          <span className="meta">PAST + UPCOMING</span>
        </header>
        <div className="featured">
          {FEATURED_GUESTS.map((g) => {
            const href = g.wikipedia ?? g.ra;
            return href ? (
              <a
                key={g.name}
                href={href}
                target="_blank"
                rel="noreferrer"
                title={g.tagline}
              >
                {g.name}
              </a>
            ) : (
              <span key={g.name} title={g.tagline}>
                {g.name}
              </span>
            );
          })}
        </div>
      </section>

      <section className="sec">
        <header className="sec-h">
          <span className="num">/ 04</span>
          <span className="title">SYSTEM HISTORY</span>
          <span className="meta">2019 → 2026</span>
        </header>
        <HomeTimeline />
      </section>

      <section className="sec">
        <header className="sec-h">
          <span className="num">/ 05</span>
          <span className="title">{t('label.artists')}</span>
          <Link to="/artists" className="meta">
            {t('cta.viewAll')} →
          </Link>
        </header>
        <ResidentsPreview />
      </section>
    </>
  );
}
